import { memo, useEffect, useMemo, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useTheme } from '@mui/material/styles';
import type { Element, ElementContent } from 'hast';
import type { Components } from 'hast-util-to-jsx-runtime';
import { parseMarkdown } from '../../markdown/pipeline';
import { hastToReact } from '../../markdown/render/hastToReact';
import { hasFootnoteMarker } from '../../markdown/plugins/rehypeFeatureGates';
import { highlightMatches } from '../../markdown/search/highlightMatches';
import { markdownSx } from '../../theme/markdownStyles';
import { resolveViewerOptions } from '../../config/viewerOptions';
import CodeBlock from '../blocks/CodeBlock';
import MermaidBlock from '../blocks/MermaidBlock';
import Callout from '../blocks/Callout';
import MarkdownTable from '../blocks/MarkdownTable';
import MarkdownLink from '../blocks/MarkdownLink';
import MarkdownImage from '../blocks/MarkdownImage';
import MarkdownHeading from '../blocks/MarkdownHeading';
import BlockQuote from '../blocks/BlockQuote';
import TaskCheckbox from '../blocks/TaskCheckbox';
import Footnotes from '../blocks/Footnotes';
import BlockErrorBoundary from './BlockErrorBoundary';
import type { Heading, MarkdownViewerProps } from '../../types';

type NodeProps = {
  node?: Element;
  children?: ReactNode;
  className?: string;
  [key: string]: unknown;
};

const textOf = (node: ElementContent | Element | undefined): string => {
  if (!node) return '';
  if (node.type === 'text') return node.value;
  if ('children' in node) return node.children.map(textOf).join('');
  return '';
};

const classNames = (node?: Element): string[] => {
  const value = node?.properties?.className;
  if (value == null) return [];
  if (Array.isArray(value)) return value.map(String);
  return String(value).split(/\s+/).filter(Boolean);
};

const languageOf = (node?: Element): string => {
  const match = classNames(node).find((name) => name.startsWith('language-'));
  return match ? match.slice('language-'.length) : '';
};

const alignOf = (node?: Element): 'left' | 'center' | 'right' | undefined => {
  const align = node?.properties?.align;
  return align === 'left' || align === 'center' || align === 'right' ? align : undefined;
};

/** Parses ```lang title="setup.ts" fences into a caption. */
const titleFromMeta = (meta: unknown): string | undefined => {
  if (typeof meta !== 'string') return undefined;
  const quoted = /(?:title|file|filename)\s*=\s*["']([^"']+)["']/i.exec(meta);
  if (quoted) return quoted[1];
  const bare = /(?:title|file|filename)\s*=\s*(\S+)/i.exec(meta);
  return bare ? bare[1] : undefined;
};

export interface MarkdownRendererProps
  extends Pick<MarkdownViewerProps, 'content' | 'options' | 'baseUrl' | 'searchTerm'> {
  onHeadingsChange?: (headings: Heading[]) => void;
  onMatchCountChange?: (count: number) => void;
  className?: string;
}

/**
 * Turns Markdown into React elements.
 *
 * Pure rendering only -- no chrome, no navigation. Parsing is memoized on the
 * source and options so typing in the search box re-highlights without
 * re-parsing the document.
 */
export const MarkdownRenderer = memo(function MarkdownRenderer({
  content,
  options,
  baseUrl,
  searchTerm,
  onHeadingsChange,
  onMatchCountChange,
  className,
}: MarkdownRendererProps) {
  const theme = useTheme();
  const resolved = useMemo(() => resolveViewerOptions(options), [options]);

  const parsed = useMemo(() => parseMarkdown(content, resolved), [content, resolved]);

  const { tree, matchCount } = useMemo(() => {
    if (!resolved.enableSearch || !searchTerm) return { tree: parsed.tree, matchCount: 0 };
    return highlightMatches(parsed.tree, searchTerm);
  }, [parsed.tree, searchTerm, resolved.enableSearch]);

  useEffect(() => onHeadingsChange?.(parsed.headings), [parsed.headings, onHeadingsChange]);
  useEffect(() => onMatchCountChange?.(matchCount), [matchCount, onMatchCountChange]);

  const components = useMemo<Partial<Components>>(() => {
    const heading = (level: 1 | 2 | 3 | 4 | 5 | 6) => {
      const Heading = ({ node, children }: NodeProps) => (
        <MarkdownHeading level={level} id={node?.properties?.id as string | undefined}>
          {children}
        </MarkdownHeading>
      );
      Heading.displayName = `MarkdownHeadingH${level}`;
      return Heading;
    };

    return {
      h1: heading(1),
      h2: heading(2),
      h3: heading(3),
      h4: heading(4),
      h5: heading(5),
      h6: heading(6),

      a: ({ node, children }: NodeProps) => (
        <MarkdownLink
          href={node?.properties?.href as string | undefined}
          title={node?.properties?.title as string | undefined}
          id={node?.properties?.id as string | undefined}
          footnoteRef={node ? hasFootnoteMarker(node, 'dataFootnoteRef') : false}
          footnoteBackref={node ? hasFootnoteMarker(node, 'dataFootnoteBackref') : false}
          allowExternal={resolved.allowExternalLinks}
          baseUrl={baseUrl}
        >
          {children}
        </MarkdownLink>
      ),

      img: ({ node }: NodeProps) => (
        <MarkdownImage
          src={node?.properties?.src as string | undefined}
          alt={(node?.properties?.alt as string | undefined) ?? ''}
          title={node?.properties?.title as string | undefined}
          width={node?.properties?.width as number | string | undefined}
          height={node?.properties?.height as number | string | undefined}
          allowExternal={resolved.allowExternalImages}
          baseUrl={baseUrl}
        />
      ),

      pre: ({ node, children }: NodeProps) => {
        const code = node?.children.find(
          (child): child is Element => child.type === 'element' && child.tagName === 'code',
        );

        if (!code) return <pre>{children}</pre>;

        const source = textOf(code).replace(/\n$/, '');
        const language = languageOf(code);

        if (resolved.enableMermaid && language === 'mermaid') {
          return (
            <BlockErrorBoundary label="Mermaid diagram">
              <MermaidBlock code={source} />
            </BlockErrorBoundary>
          );
        }

        return (
          <BlockErrorBoundary label="code block">
            <CodeBlock
              code={source}
              language={resolved.enableSyntaxHighlighting ? language : 'text'}
              title={titleFromMeta(code.properties?.dataMeta)}
              showLineNumbers={resolved.showLineNumbers}
              wrap={resolved.wrapCode}
            />
          </BlockErrorBoundary>
        );
      },

      code: ({ children, className: cls }: NodeProps) => (
        <Box
          component="code"
          className={cls}
          sx={{
            fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: '0.86em',
            padding: '0.15em 0.4em',
            borderRadius: '5px',
            backgroundColor: theme.markdown.inlineCodeBackground,
            color: theme.markdown.inlineCodeText,
            overflowWrap: 'break-word',
          }}
        >
          {children}
        </Box>
      ),

      blockquote: ({ node, children }: NodeProps) => {
        const callout = node?.properties?.dataCallout;
        if (typeof callout === 'string') {
          return (
            <Callout type={callout} title={node?.properties?.dataCalloutTitle as string | undefined}>
              {children}
            </Callout>
          );
        }
        return <BlockQuote>{children}</BlockQuote>;
      },

      div: ({ node, children, ...rest }: NodeProps) => {
        const callout = node?.properties?.dataCallout;
        if (typeof callout === 'string') {
          return (
            <Callout type={callout} title={node?.properties?.dataCalloutTitle as string | undefined}>
              {children}
            </Callout>
          );
        }
        return (
          <div id={node?.properties?.id as string | undefined} className={rest.className as string | undefined}>
            {children}
          </div>
        );
      },

      table: ({ children }: NodeProps) =>
        resolved.enableTables ? (
          <MarkdownTable>{children}</MarkdownTable>
        ) : (
          <table>{children}</table>
        ),
      thead: ({ children }: NodeProps) => <TableHead>{children}</TableHead>,
      tbody: ({ children }: NodeProps) => <TableBody>{children}</TableBody>,
      tr: ({ children }: NodeProps) => <TableRow>{children}</TableRow>,
      th: ({ node, children }: NodeProps) => (
        <TableCell component="th" scope="col" variant="head" align={alignOf(node)}>
          {children}
        </TableCell>
      ),
      td: ({ node, children }: NodeProps) => (
        <TableCell variant="body" align={alignOf(node)}>
          {children}
        </TableCell>
      ),

      input: ({ node }: NodeProps) => {
        if (node?.properties?.type !== 'checkbox') return null;
        return (
          <TaskCheckbox
            checked={Boolean(node.properties.checked)}
            interactive={resolved.interactiveTaskLists}
          />
        );
      },

      section: ({ node, children, ...rest }: NodeProps) => {
        if (node && hasFootnoteMarker(node, 'dataFootnotes')) {
          return <Footnotes>{children}</Footnotes>;
        }
        return (
          <section id={node?.properties?.id as string | undefined} className={rest.className as string | undefined}>
            {children}
          </section>
        );
      },
    } as unknown as Partial<Components>;
  }, [resolved, baseUrl, theme]);

  const rendered = useMemo(
    () => hastToReact(tree, { components }),
    [tree, components],
  );

  return (
    <Box
      component="article"
      className={['markdown-body', className].filter(Boolean).join(' ')}
      sx={markdownSx(theme)}
    >
      {rendered}
    </Box>
  );
});

export default MarkdownRenderer;
